import { flash } from "@xola/ui-kit";
import clsx from "clsx";
import {
    allItems,
    bookingEnginePurchaseActions,
    DateFormat,
    formatDate,
    getActionsByVariant,
    getAddonCatalog,
    getMembershipDiscountLineItem,
    getMenuVariant,
    getPaymentLineItems,
    getPurchaseItemsHavingMembershipCodeDiscount,
    isEquipmentBased,
    isEquipmentItem,
    isEveryExperiencePurchaseItemCanceled,
    isEveryItemCanceled,
    isExperiencePurchaseItem,
    isGiftPurchaseItem,
    isMerchandiseItem,
    isMistPurchase,
    isOtherSellerPurchase,
    isPackageItem,
    isPurchaseItemCanceled,
    isScheduled,
    isStoreCredit,
    ItemActionBar,
    ItemActionOption,
    now,
    PurchaseActionOption,
    PurchaseItemStatus,
    RosterActionOption,
} from "common";
import { isArray, isEmpty, sumBy, uniqueId } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import { useHistory, useLocation } from "react-router-dom";
import {
    Affiliate,
    CommunicationChannelType,
    EmailTemplate,
    Event,
    Experience,
    ExperiencePurchaseItem,
    GiftPurchaseItem,
    MerchandisePurchaseItem,
    NotificationPreferenceType,
    PartnerObject,
    Permission,
    Purchase,
    PurchaseItem,
    PurchaseItemGuestStatus,
    PurchaseItemObject,
} from "types";
import {
    isPackage as checkPackage,
    getGratuityEligibleItems,
    getPurchaseBalanceForRefund,
    getStoreCreditLineItemsForReverseFunding,
    isOneBuyerWithSamePaymentMethod,
    isStoreCreditWithAddedFunds,
} from "../../models/Purchase";
import { isAllowedPrivacy, isBookingEngine, isPurchaseItemWithAddon } from "../../models/PurchaseItem";
import { useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { updateCache } from "../../utils/updateCache";
import { Printer } from "../Common/Printer";
import { useSourceMessagingPlugin, useSourcePlugin } from "../Common/SourceIcon";
import { CancelPurchaseItemModal } from "../Dashboard/CancelPurchaseItemModal";
import { RefundOption } from "../Dashboard/RefundOption";
import { RefundOptionsModal } from "../Dashboard/RefundOptionsModal";
import { UnassignEventGuidePrompt } from "../Dashboard/Resources/Guides/UnassignEventGuides";
import { SplitPackageModal } from "../Dashboard/SplitPackageModal";
import { SwitchExperienceModal } from "../Dashboard/SwitchExperienceModal";
import { AddFundsGiftModal } from "../PurchaseModification/AddFundsGiftModal";
import { AdjustBalanceModal } from "../PurchaseModification/AdjustBalanceModal";
import { PurchaseChangeQuantityModal } from "../PurchaseModification/ChangeQuantity/PurchaseChangeQuantityModal";
import { CollectBalanceOption, CollectBalanceOptionsModal } from "../PurchaseModification/CollectBalanceDeciderModal";
import { CollectPurchaseBalanceViaInvoiceModal } from "../PurchaseModification/CollectBalanceViaInvoiceModal";
import { CollectPurchaseBalanceModal } from "../PurchaseModification/CollectPurchaseBalanceModal";
import { CreatePurchaseNoteModal } from "../PurchaseModification/CreatePurchaseNoteModal";
import { EditPurchaseAddOnsModal } from "../PurchaseModification/EditPurchaseAddOnsModal";
import { EditPurchasePrivacyModal } from "../PurchaseModification/EditPurchasePrivacyModal";
import { UpdateFulfilledQuantityModal } from "../PurchaseModification/MerchandiseFulfillment/UpdateFulfilledQuantityModal";
import { MessagePurchaseModal } from "../PurchaseModification/MessagePurchaseModal";
import { PurchaseChangeArrivalModal } from "../PurchaseModification/PurchaseChangeArrivalModal";
import { PurchaseManualRefundModal } from "../PurchaseModification/PurchaseManualRefundModal";
import { PurchaseReduceBookingValueModal } from "../PurchaseModification/Refund/PurchaseReduceBookingValueModal";
import {
    PurchaseReturnPaymentModal,
    ReturnPaymentSource,
} from "../PurchaseModification/Refund/PurchaseReturnPaymentModal";
import { ReverseFundingModal } from "../PurchaseModification/ReverseFundingModal";
import { SplitPayRefundModal } from "../PurchaseModification/SplitPayRefundModal";
import { UpdateMerchandiseQuantityModal } from "../PurchaseModification/UpdateQuantity/UpdateMerchandiseQuantityModal";
import { PurchaseReceipt } from "../Receipt/PurchaseReceipt";
import { BookingEngineWarningModal } from "./BookingEngineWarningModal";
import { ExportTicketsModal } from "./ExportTicketsModal";
import { GiftReceiptToPrint } from "./GiftReceiptToPrint";
import { PastBookingWarningModal } from "./PastBookingWarningModal";
import { api } from "@/api/api";
import { getResponseErrorMessage } from "@/api/interceptors";
import {
    getRefundOptionAfterCancel,
    shouldReturnExcessAtPurchaseLevel,
} from "@/components/Dashboard/getRefundOptionAfterCancel";
import { RemoveMembershipCodeModal } from "@/components/Dashboard/RemoveMembershipCodeModal";
import { EditGratuityModal } from "@/components/PurchaseModification/EditGratuityModal";
import { EmbeddedAppTriggerLocation, findTriggersAtLocation } from "@/embeddedApps/EmbeddedAppTriggerLocation";
import { usePurchaseExperiences } from "@/hooks/resources/useProduct";
import { useTraveler } from "@/hooks/resources/useTraveler";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useInvalidate } from "@/hooks/useInvalidation";
import { usePostCheckinWarningModal } from "@/hooks/usePostCheckinWarningModal";
import { useResource } from "@/hooks/useResource";
import { useModal } from "@/hooks/useVisibility";
import { hasBookings, isEventReservedByItem } from "@/models/Event";
import { allowMultipleEventsPerTimeslot, isGratuityForExperienceEnabled } from "@/models/Experience";
import { useInstalledPluginsFromStore } from "@/stores/globalStore";

const BUTTON_BAR_LOCATION = "purchases.detail.buttonBar";

interface Conversation {
    id: ID;
    purchase: { id: ID };
}

interface PurchaseItemActionsBarProps {
    className?: string;
    purchase: Purchase;
    item?: PurchaseItem;
    experience?: Experience;
    event?: Event; // Passing event optionally instead of using useResource to resolve infinite rendering issue - X2-7087
    isLoading?: boolean;
}

export const PurchaseItemActionsBar = ({
    className,
    purchase,
    item: selectedItem,
    experience,
    event,
    isLoading = false,
}: PurchaseItemActionsBarProps) => {
    const seller = useSeller();
    const history = useHistory();
    const location = useLocation();
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();
    const plugin = useSourcePlugin(purchase.source);
    const messagingPlugin = useSourceMessagingPlugin(purchase.source);
    const [selectedOption, setSelectedOption] = useState<PurchaseActionOption | null>(null);
    const [selectedRefundOption, setSelectedRefundOption] = useState<RefundOption | null>(null);
    const [returnPaymentSource, setReturnPaymentSource] = useState<ReturnPaymentSource | null>(null);
    const [refundItem, setRefundItem] = useState<PurchaseItem | null>(null);
    const [manualRefundAmount, setManualRefundAmount] = useState(0);
    const [refundAmount, setRefundAmount] = useState<number | null>(0);
    const [stayModalOpen, setStayModalOpen] = useState(true);
    const [warningModalOpen, setWarningModalOpen] = useState(false);
    const bookingEngineWarningModalOpen = useModal(false);
    const [remainingAction, setRemainingAction] = useState<PurchaseActionOption | RosterActionOption | null>(null);
    const resetState = useCreatePurchaseStore((store) => store.actions.resetState);
    const [shouldPrintStart, setShouldPrintStart] = useState(false);
    const [returnAll, setReturnAll] = useState(false);
    const [isCancelAction, setIsCancelAction] = useState(false);
    const [modifiedPurchase, setModifiedPurchase] = useState<Purchase | null>(null);
    const [previousEvent, setPreviousEvent] = useState<Event | Event[] | null>(null);
    const experiences = usePurchaseExperiences(purchase);
    const firstGratuityEligibleItem = getGratuityEligibleItems(purchase)[0];

    const item = (selectedItem ?? purchase.items[0]) as ExperiencePurchaseItem;
    const isPackage = checkPackage(item);
    const isMist = isMistPurchase(purchase);
    const isMistPurchasePage = !selectedItem && isMist;

    // When cancelling a MIST item that has no value left, drop the item so the modal returns the
    // purchase-level overpayment instead of the item-scoped modal, which would cap the refund at $0.
    const returnExcessAtPurchaseLevel = shouldReturnExcessAtPurchaseLevel(
        modifiedPurchase ?? purchase,
        selectedItem ?? refundItem,
        selectedRefundOption,
    );
    const refundModalItem = returnExcessAtPurchaseLevel
        ? undefined
        : ((selectedItem ?? refundItem) as ExperiencePurchaseItem);
    const itemRefundAmount = refundAmount === null ? null : Math.abs(refundAmount);
    const refundModalAmount = returnExcessAtPurchaseLevel
        ? Math.abs((modifiedPurchase ?? purchase).balance)
        : itemRefundAmount;
    const popoverParentId = uniqueId("popoverParentId");
    /** TODO: When user cancels the reservation, we are storing the callback data here if "Remove membership code" modal is applicable, and once that is continued, we use data from here, find a better way for this */
    const [cancelReservationModalData, setCancelReservationModalData] = useState<{
        showRefundModal: boolean;
        newPurchase?: Purchase;
        events?: Event[];
    } | null>(null);

    const hasCustomerEmail = !!purchase.customerEmail;
    const enabledChannels = seller.preferences.communicationChannels?.enabledChannels ?? [];
    const shouldSendSms = enabledChannels.includes(CommunicationChannelType.SMS) && !!purchase.phone;
    const isAffiliatePurchase = !!(purchase.partner?.id && purchase.partner?.object === PartnerObject.AFFILIATE);

    const { PostCheckInWarningModal, confirmPostCheckInWarning } = usePostCheckinWarningModal(item);

    const menuVariant = getMenuVariant(purchase, selectedItem);

    const { traveler: organizer, isLoadingTraveler: isLoadingOrganizer } = useTraveler(
        purchase.organizer?.id,
        null,
        {
            staleTime: 0,
            enabled: isGranted(Permission.MODIFY_BOOKING_LITE) && !isOtherSellerPurchase(purchase, seller.id),
        }, // default stale time is 24 hours so we need to use 0 here because we want to fetch organizer every time
    );

    const { data: affiliate, isLoading: isLoadingAffiliate } = useResource<Affiliate>(
        ["affiliates", purchase.partner?.id],
        null,
        {
            enabled: isAffiliatePurchase,
        },
    );
    const canAffiliateReceiveEmail = !!affiliate?.affiliateNotification?.enabled && !!affiliate?.email;

    const { mutateAsync: sendCertificate } = useMutation(async () => {
        try {
            const conversationId = await getConversationId();
            await api.post(`/conversations/${conversationId}/messages`, {
                meta: {
                    template: EmailTemplate.PURCHASE_CONFIRMATION,
                    itemId: item.id,
                },
                createdAt: formatDate(now(), DateFormat.DATE_TIME_UTC_ISO),
            });
            flash.success("Certificate sent.");
        } catch {
            flash.danger("Error sending certificate");
        }
    });

    const { mutateAsync: sendReceiptEmail } = useMutation(async () => {
        try {
            const conversationId = await getConversationId();
            await api.post(`/conversations/${conversationId}/messages`, {
                meta: {
                    template: EmailTemplate.PURCHASE_RECEIPT,
                    itemId: item.id,
                },
                createdAt: formatDate(now(), DateFormat.DATE_TIME_UTC_ISO),
            });
            flash.success("Success.");
        } catch {
            flash.danger("Error sending receipt email");
        }
    });

    const { mutateAsync: resendConfirmationEmail } = useMutation(async () => {
        try {
            const payload: any = {
                meta: { template: EmailTemplate.PURCHASE_CONFIRMATION },
                to: { id: purchase.organizer?.id, type: "traveler" },
            };
            if (selectedItem) {
                payload.meta.itemId = selectedItem.id;
            }

            const conversationId = await getConversationId();
            return await api.post(`/conversations/${conversationId}/messages`, payload);
        } catch (error) {
            console.error("Error sending confirmation email");
            console.log(error);
            throw error;
        }
    });

    const { mutateAsync: resendConfirmationSms } = useMutation(async () => {
        try {
            const payload: any = {
                meta: { template: EmailTemplate.PURCHASE_CONFIRMATION },
                to: { id: purchase.organizer?.id, type: "traveler" },
                type: "sms",
            };
            if (selectedItem) {
                payload.meta.itemId = selectedItem.id;
            }

            const conversationId = await getConversationId();
            return await api.post(`/conversations/${conversationId}/messages`, payload);
        } catch (error) {
            console.error("Error sending confirmation sms");
            console.log(error);
            throw error;
        }
    });

    const { mutateAsync: sendGiftCertificateToRecipient } = useMutation(async (purchaseItem: GiftPurchaseItem) => {
        try {
            const conversationId = await getConversationId();
            await api.post(`/conversations/${conversationId}/messages`, {
                meta: { template: EmailTemplate.PURCHASE_CONFIRMATION, itemId: purchaseItem.id },
                to: { id: purchaseItem?.recipient?.id },
            });
            flash.success("Gift Certificate sent to recipient");
        } catch {
            flash.danger("Error sending gift certificate to recipient");
        }
    });

    const giftOrStoreCreditEmails = new Set([
        PurchaseActionOption.SEND_GIFT_RECEIPT_EMAIL,
        PurchaseActionOption.SEND_STORE_CREDIT_EMAIL,
    ]);

    const handleButtonClick = async (action: PurchaseActionOption | RosterActionOption, purchaseItem: PurchaseItem) => {
        if (action === PurchaseActionOption.SEND_CERTIFICATE) {
            await sendCertificate();
            return;
        }

        if (giftOrStoreCreditEmails.has(action as PurchaseActionOption)) {
            await sendReceiptEmail();
            return;
        }

        if (action === PurchaseActionOption.RESEND_CONFIRMATION) {
            const messagesSent = [];
            const messagesFailed: Array<{ channel: CommunicationChannelType; message: string }> = [];

            if (hasCustomerEmail || canAffiliateReceiveEmail) {
                try {
                    await resendConfirmationEmail();
                    messagesSent.push(CommunicationChannelType.EMAIL);
                } catch (error: any) {
                    messagesFailed.push({
                        channel: CommunicationChannelType.EMAIL,
                        message: getResponseErrorMessage(error.response),
                    });
                }
            }

            if (shouldSendSms) {
                try {
                    await resendConfirmationSms();
                    messagesSent.push(CommunicationChannelType.SMS);
                } catch {
                    messagesFailed.push({
                        channel: CommunicationChannelType.SMS,
                        message: "",
                    });
                }
            }

            if (messagesSent.length > 0) {
                flash.success("Confirmation " + messagesSent.join(" and ") + " sent successfully");
                return;
            }

            if (
                messagesFailed.length === 1 &&
                messagesFailed[0].channel === CommunicationChannelType.EMAIL &&
                messagesFailed[0].message
            ) {
                // If only email channel is enabled, and the API throws error, need to show error message directly from API response
                flash.danger(messagesFailed[0].message);
                return;
            }

            if (messagesFailed.length > 0) {
                const channels = messagesFailed.map((message) => message.channel).join(" and ");
                flash.danger(`Failed sending confirmation ${channels}`);
            }

            return;
        }

        if (action === PurchaseActionOption.PRINT_RECEIPT) {
            setShouldPrintStart(true);
            return;
        }

        // Before we open modal from action
        // we will check if it's needed to open warning modal

        const isPastDateArrival =
            "arrivalDateTime" in purchaseItem && purchaseItem.arrivalDateTime?.isBefore(now(), "day");

        const isCheckedIn =
            purchaseItem.guestStatus === PurchaseItemGuestStatus.PARTIALLY_ARRIVED ||
            purchaseItem.guestStatus === PurchaseItemGuestStatus.ARRIVED;

        const excludedNonModificationActions = new Set([
            PurchaseActionOption.REBOOK_ORGANIZER,
            PurchaseActionOption.EMAIL_RECEIPT,
            PurchaseActionOption.RESEND_GRATUITY_EMAIL,
            PurchaseActionOption.RESEND_WAIVER_EMAIL,
        ]);

        if (!isBookingEngine(purchase.source) && !excludedNonModificationActions.has(action as PurchaseActionOption)) {
            if (isCheckedIn) {
                const confirmation = await confirmPostCheckInWarning();
                if (!confirmation) return;
            }

            if (isPastDateArrival) {
                setRemainingAction(action as PurchaseActionOption);
                setWarningModalOpen(true);
                return;
            }
        }

        if (isBookingEngine(purchase.source) && !excludedNonModificationActions.has(action as PurchaseActionOption)) {
            bookingEngineWarningModalOpen.show();
            setRemainingAction(action as PurchaseActionOption);
            return;
        }

        if (action === PurchaseActionOption.REFUND && isStoreCredit(purchase)) {
            // if funds has been added to store credit manually
            const fundsAdded = isStoreCreditWithAddedFunds(purchase);

            // if the store credit have lineItems for reverse funding
            const hasReverseFundingItems = getStoreCreditLineItemsForReverseFunding(purchase).length > 0;

            if (fundsAdded && hasReverseFundingItems) {
                // the dialog of two options to choice will be shown
            } else if (fundsAdded) {
                setSelectedRefundOption(RefundOption.REFUND_PAYMENT);
                return;
            } else if (hasReverseFundingItems) {
                setSelectedRefundOption(RefundOption.REVERSE_FUNDING);
                return;
            } else {
                // impossible, the 'Refund' button had to be disabled
            }
        }

        if (action === PurchaseActionOption.REFUND_GIFT) {
            setSelectedRefundOption(RefundOption.REDUCE_AND_RETURN);
            const amount = purchase.balance < 0 ? purchase.balance : sumBy(getPaymentLineItems(purchase), "amount");
            setRefundAmount(Math.abs(amount));
            return;
        }

        setIsCancelAction(action === PurchaseActionOption.CANCEL_RESERVATION);

        setSelectedOption(action as PurchaseActionOption);

        if (action === PurchaseActionOption.REBOOK_ORGANIZER) {
            resetState();
            useCreatePurchaseStore.setState({ organizer: organizer });
            history.push("/cart/init");
        }

        if (action === PurchaseActionOption.RESEND_WAIVER_EMAIL) {
            resendWaiverEmail();
            flash.success("Waiver email sent successfully");
        }

        if (action === PurchaseActionOption.RESEND_GRATUITY_EMAIL) {
            resendGratuityEmail();
            flash.success("Gratuity notification sent successfully");
        }

        if (action === PurchaseActionOption.EMAIL_RECEIPT) {
            try {
                await sendEmailReceipt();
                flash.success("Email Receipt Sent");
                invalidate("purchases", purchase.id);
            } catch (error: any) {
                const errorMessage = getResponseErrorMessage(error.response);
                if (errorMessage) {
                    flash.danger(errorMessage);
                }
            }
        }

        if (action === PurchaseActionOption.SEND_GIFT_CERTIFICATE_TO_RECIPIENT) {
            await sendGiftCertificateToRecipient(purchaseItem as GiftPurchaseItem);
            return;
        }

        if (action === PurchaseActionOption.SEND_MEMBERSHIP_EMAIL) {
            try {
                await resendConfirmationEmail();
                flash.success("Membership email sent successfully");
            } catch {
                flash.danger("Failed sending membership email");
            }
        }
    };

    const getConversationId = async () => {
        if (!purchase.conversation?.id) {
            const conversation = await createConversation();
            return conversation.id;
        }

        return purchase.conversation.id;
    };

    const isDisabled = (
        action?: PurchaseActionOption | RosterActionOption,
        activeOnly?: boolean,
        experience?: Experience,
    ) => {
        const canResendWaiverEmail = seller.preferences.notifications
            .find((item) => item.type === NotificationPreferenceType.WAIVER_REMINDER_TRAVELER)
            ?.channels.some((channel) => channel.type === "email" && channel.enabled);

        const canSkipSMS = purchase.phone && !isGranted(Permission.SMS_SEND);
        const isEmailAvailable = !!organizer?.email || !!purchase.customerEmail;
        if (giftOrStoreCreditEmails.has(action as PurchaseActionOption)) {
            return !isEmailAvailable;
        }

        const hasAddOnsConfigured = experiences.some((experience) => getAddonCatalog(experience).length > 0);

        const isOneOrAllItemsCanceled =
            activeOnly && ((selectedItem && isPurchaseItemCanceled(selectedItem)) || isEveryItemCanceled(purchase));

        const isOneOrAllExperiencePurchaseItemsCancelled =
            activeOnly &&
            ((selectedItem && isPurchaseItemCanceled(selectedItem)) || isEveryExperiencePurchaseItemCanceled(purchase));

        switch (action) {
            case PurchaseActionOption.EMAIL_RECEIPT:
                return purchase.items.length === 0 || !isEmailAvailable;

            case PurchaseActionOption.RESEND_WAIVER_EMAIL:
                return !experience?.waiverPreference || !isEmailAvailable || !canResendWaiverEmail;

            case PurchaseActionOption.EDIT_PURCHASE_PRIVACY: {
                if (!experience) {
                    return true;
                }

                // Equipment bookings reserve resources, not seats, so `quantity.reserved` is never set for them.
                const isReservedByItem = isEquipmentBased(experience)
                    ? isEventReservedByItem(item.id, event)
                    : event?.quantity?.reserved === item.quantity;
                const showPrivacyChange =
                    (experience?.resources && allowMultipleEventsPerTimeslot(seller, experience)) || isReservedByItem;
                return (
                    (experience?.allowedPrivacies?.length === 1 &&
                        selectedItem &&
                        isAllowedPrivacy(selectedItem?.privacy) &&
                        experience.allowedPrivacies.includes(selectedItem?.privacy)) ||
                    !showPrivacyChange
                );
            }

            case PurchaseActionOption.RESEND_GRATUITY_EMAIL:
                return !isMistPurchasePage && (!experience || !isGratuityForExperienceEnabled(seller, experience.id));

            case PurchaseActionOption.MESSAGE:
                return (
                    (!isEmailAvailable && (canSkipSMS || !purchase.phone) && !messagingPlugin) ||
                    (selectedItem && isPurchaseItemCanceled(selectedItem)) ||
                    isEveryItemCanceled(purchase)
                );

            case PurchaseActionOption.EDIT_ADD_ONS:
                return selectedItem && experience
                    ? !isPurchaseItemWithAddon(selectedItem, experience) && getAddonCatalog(experience).length === 0
                    : !hasAddOnsConfigured;

            case PurchaseActionOption.REFUND:
                return purchase.amount <= 0;

            case PurchaseActionOption.EXPORT_TICKET:
                return !purchase.items.some((index) => index.ticketCode);

            case PurchaseActionOption.SEND_CERTIFICATE:
                return !isEmailAvailable || !!isOneOrAllItemsCanceled;

            case PurchaseActionOption.SEND_GIFT_CERTIFICATE_TO_RECIPIENT: {
                const giftItem = selectedItem ?? purchase.items[0];
                return !isGiftPurchaseItem(giftItem) || !giftItem.recipient?.id || !giftItem.recipientEmail;
            }

            case PurchaseActionOption.REBOOK_ORGANIZER:
                // The cart is seeded from the loaded organizer, so rebooking early starts it without one
                return !experience || !purchase.organizer?.id || isLoadingOrganizer;

            case PurchaseActionOption.CHANGE_ARRIVAL:
            case PurchaseActionOption.MODIFY_GUESTS:
            case PurchaseActionOption.SWITCH_EXPERIENCE:
                return isOneOrAllExperiencePurchaseItemsCancelled;

            case PurchaseActionOption.UPDATE_QUANTITY:
            case PurchaseActionOption.UPDATE_FULFILLED_QUANTITY:
            case PurchaseActionOption.CANCEL_RESERVATION:
                return (selectedItem && isPurchaseItemCanceled(selectedItem)) || isEveryItemCanceled(purchase);

            default:
                return (
                    activeOnly &&
                    ((selectedItem && isPurchaseItemCanceled(selectedItem)) || isEveryItemCanceled(purchase))
                );
        }
    };

    const getActions = () => {
        const allMenuItems = allItems(purchase, seller.id, selectedItem, false);
        const actionsByTypeMenu = getActionsByVariant(menuVariant, purchase);
        const buttons: ItemActionOption[] = [];
        const isBookingEnginePurchase = selectedItem?.source && !!isBookingEngine(selectedItem.source);

        const canSendConfirmation = !!(purchase.customerEmail || purchase.phone) || canAffiliateReceiveEmail;

        for (const menuItem of allMenuItems) {
            if (actionsByTypeMenu.includes(menuItem.action as PurchaseActionOption)) {
                if ((menuItem.permission && !isGranted(menuItem.permission)) || menuItem.exclude) {
                    continue;
                } else if (
                    !seller.preferences.booking.allowBookingEngineOrderModification &&
                    isBookingEnginePurchase &&
                    !bookingEnginePurchaseActions.includes(menuItem.action as PurchaseActionOption)
                ) {
                    continue;
                } else if (isPackageItem(selectedItem) && item.status === PurchaseItemStatus.CANCELED) {
                    continue;
                } else if (isDisabled(menuItem.action, menuItem.activeOnly, experience)) {
                    continue;
                }

                const isResendConfirmation = menuItem.action === PurchaseActionOption.RESEND_CONFIRMATION;
                const isChangeArrival = menuItem.action === PurchaseActionOption.CHANGE_ARRIVAL;
                const isModifyGuests = menuItem.action === PurchaseActionOption.MODIFY_GUESTS;
                const hasSchedules = selectedItem
                    ? !!experience && isScheduled(experience)
                    : purchase.items.some(
                          (item) => item.object === PurchaseItemObject.EXPERIENCE || isEquipmentItem(item),
                      );
                const isExperience = selectedItem
                    ? isExperiencePurchaseItem(selectedItem)
                    : purchase.items.some((item) => isExperiencePurchaseItem(item));

                if (isChangeArrival && !hasSchedules) {
                    continue;
                }

                if (isModifyGuests && !isExperience) {
                    continue;
                }

                if (isResendConfirmation && !canSendConfirmation) {
                    continue;
                }

                buttons.push(menuItem);
            }
        }

        return buttons;
    };

    // Held empty only while missing data could show a wrong action. The event and organizer only ever add actions
    // (the organizer's email matters only without a customer email), so waiting on them would just delay the bar.
    const isLoadingActions = isLoading || isLoadingAffiliate || (isLoadingOrganizer && !purchase.customerEmail);
    const actions = isLoadingActions ? [] : getActions();
    // Read the shared snapshot, not the live query: this bar mounts once per purchase item, and
    // findTriggersAtLocation is the same rule EmbeddedAppTriggerLocation applies, so the bar's
    // visibility cannot diverge from what the trigger actually renders.
    const plugins = useInstalledPluginsFromStore();
    const embeddedAppsAtButtonBar = useMemo(() => findTriggersAtLocation(plugins, BUTTON_BAR_LOCATION), [plugins]);

    const buttonBarContext = useMemo(() => {
        return {
            purchases: [
                {
                    id: purchase.id,
                    items: [
                        {
                            id: item.id,
                            guests: (item.guests ?? []).map((g) => ({ id: g.id })),
                        },
                    ],
                },
            ],
        };
    }, [purchase.id, item.id, item.guests]);

    const { mutateAsync: resendWaiverEmail } = useMutation(async () => {
        const conversationId = await getConversationId();
        return api.post(`/conversations/${conversationId}/messages`, {
            meta: { template: EmailTemplate.PURCHASE_REMINDER_WAIVER, itemId: item.id },
        });
    });

    const { mutateAsync: sendEmailReceipt } = useMutation(async () => {
        const conversationId = await getConversationId();
        return api.post(`/conversations/${conversationId}/messages`, {
            meta: { template: EmailTemplate.PURCHASE_RECEIPT },
        });
    });

    const { mutateAsync: resendGratuityEmail } = useMutation(async () => {
        const conversationId = await getConversationId();
        return api.post(`/conversations/${conversationId}/messages`, {
            meta: { template: "gratuity_purchase_item_reminder_traveler", itemId: item.id },
        });
    });

    const { mutateAsync: createConversation } = useMutation(async () => {
        const { data } = await api.post<Conversation>("/conversations", {
            purchase: { id: purchase.id },
            seller: { id: seller.id },
        });
        return data;
    });

    //
    // TODO: Too many modals and mutations in this one giant component. Refactor into smaller pieces
    //

    const resetSelectedRefundOption = () => {
        setReturnPaymentSource(null);
        setSelectedRefundOption(null);
        invalidate("purchases", purchase.id);
    };

    const resetSelectedOption = () => setSelectedOption(null);

    const isCollectBalanceOptionSelected = selectedOption === PurchaseActionOption.COLLECT_BALANCE;

    const handleFlushPurchaseCache = () => {
        invalidate("timeslots");
        if (item.event) {
            invalidate("events", item.event.id);
        }

        updateCache("purchases", purchase);
        setModifiedPurchase(null);
    };

    const shouldShowUnassignGuide = event && event.guides.length > 0 && event.quantity.reserved === 0;

    const handleCloseAfterPurchaseChange = (
        newPurchase: Purchase | undefined,
        nextReturnPaymentSource: ReturnPaymentSource | null = null,
        nextRefundItem?: PurchaseItem,
    ) => {
        // Purchase will be undefined if custom line items haven`t changed
        const hasBalance = newPurchase?.balance && newPurchase.balance > 0.01;
        const isRefund = newPurchase && newPurchase.balance < 0;

        if (hasBalance) {
            setReturnPaymentSource(null);
            setRefundItem(null);
            setModifiedPurchase(newPurchase);
            setSelectedOption(PurchaseActionOption.COLLECT_BALANCE_OPTIONS);
            return;
        }

        if (isRefund && newPurchase) {
            setModifiedPurchase(newPurchase);
            setRefundAmount(getPurchaseBalanceForRefund(purchase, newPurchase));
            setReturnPaymentSource(nextReturnPaymentSource);
            setRefundItem(nextRefundItem ?? null);
            setSelectedRefundOption(RefundOption.RETURN_PAYMENT);
            setSelectedOption(null);
            return;
        }

        if (!hasBalance && !isRefund && shouldShowUnassignGuide) {
            setReturnPaymentSource(null);
            setRefundItem(null);
            setSelectedOption(PurchaseActionOption.UNASSIGN_GUIDE);
            return;
        }

        setReturnPaymentSource(null);
        setRefundItem(null);
        setRefundAmount(0);
        setSelectedOption(null);
        // Invalidate purchase since we do not have to show next modal
        // If we have to show the next modal, we will invalidate the purchase after the payment is processed
        if (newPurchase && !("target" in newPurchase)) {
            // Checking a newPurchase to make sure it is not an MouseEvent
            invalidate("purchases", purchase?.id);
        }

        invalidate("purchaseItems", item.id);
    };

    const handleCancelReservationClose = (showRefundModal: boolean, newPurchase?: Purchase, events?: Event[]) => {
        if (!showRefundModal && shouldShowUnassignGuide && !events) {
            setPreviousEvent(event);
            setSelectedOption(PurchaseActionOption.UNASSIGN_GUIDE);
        }

        // It's crucial to have the new purchase object here, as the following will get executed sooner than the cache update
        const currentPurchase = newPurchase ?? purchase;

        if (newPurchase) {
            updateCache("purchases", newPurchase);
        }

        resetSelectedOption();

        let refundOption = null;

        if (showRefundModal) {
            setReturnAll(isEveryItemCanceled(currentPurchase));
            refundOption = getRefundOptionAfterCancel(currentPurchase, selectedItem);
        }

        setSelectedRefundOption(refundOption);
        setRefundAmount(null);

        if (showRefundModal && isMistPurchasePage && (isPackage || isMist)) {
            setReturnAll(true);
        }

        const filteredEvents = events?.filter((event) => event.quantity.reserved === 0 && event.guides.length > 0);

        if (filteredEvents && !isEmpty(filteredEvents)) {
            setPreviousEvent(filteredEvents);
        }
    };

    useEffect(() => {
        setSelectedOption(null);
        setSelectedRefundOption(null);
        setWarningModalOpen(false);
    }, [location.pathname]);

    const merchandiseItem = isMerchandiseItem(item) ? item : purchase.items.find((item) => isMerchandiseItem(item));

    const hasBarContent = isLoadingActions || actions.length > 0 || embeddedAppsAtButtonBar.length > 0;

    return (
        <div className={clsx(hasBarContent && "border-b", "border-gray-light")} id={popoverParentId}>
            {hasBarContent && (
                <ItemActionBar
                    actions={actions}
                    className={className}
                    popoverParentId={popoverParentId}
                    onButtonClick={async (actions) => handleButtonClick(actions, item)}
                >
                    <EmbeddedAppTriggerLocation
                        id={BUTTON_BAR_LOCATION}
                        context={buttonBarContext}
                        buttonVariant="link"
                        buttonSize="small"
                        buttonColor="common"
                        buttonClassName="whitespace-nowrap !text-base"
                    />
                </ItemActionBar>
            )}
            <MessagePurchaseModal
                isTravelersSelectable
                purchase={purchase}
                item={item}
                isOpen={selectedOption === PurchaseActionOption.MESSAGE}
                onClose={() => {
                    resetSelectedOption();
                    invalidate("conversations", purchase.conversation.id);
                }}
            />
            <CreatePurchaseNoteModal
                purchase={purchase}
                isOpen={selectedOption === PurchaseActionOption.ADD_PURCHASE_NOTE}
                onClose={resetSelectedOption}
            />
            <PurchaseChangeArrivalModal
                purchase={purchase}
                item={item}
                isOpen={selectedOption === PurchaseActionOption.CHANGE_ARRIVAL}
                onClose={(newPurchase, _redirectUrl, nextRefundItem) =>
                    handleCloseAfterPurchaseChange(newPurchase, ReturnPaymentSource.CHANGE_ARRIVAL, nextRefundItem)
                }
                onContinue={(changedEvent) => {
                    if (!hasBookings(changedEvent) && !isEmpty(changedEvent.guides)) {
                        setPreviousEvent(changedEvent);
                        setSelectedOption(PurchaseActionOption.UNASSIGN_GUIDE);
                        return;
                    }

                    resetSelectedOption();
                }}
            />
            {previousEvent && (
                <UnassignEventGuidePrompt
                    isOpen={selectedOption === PurchaseActionOption.UNASSIGN_GUIDE}
                    events={previousEvent}
                    onClose={() => {
                        handleFlushPurchaseCache();
                        resetSelectedOption();
                        resetSelectedRefundOption();
                        setPreviousEvent(null);
                    }}
                />
            )}
            <PurchaseChangeQuantityModal
                purchase={purchase}
                item={item}
                isOpen={
                    selectedOption === PurchaseActionOption.MODIFY_GUESTS ||
                    selectedRefundOption === RefundOption.CHANGE_GUEST_QUANTITY ||
                    selectedRefundOption === RefundOption.CHANGE_EQUIPMENT_QUANTITY
                }
                onClose={(
                    nextModalOption: PurchaseActionOption | null,
                    nextRefundOption: RefundOption | null,
                    refundAmount,
                    _newPurchase,
                    nextRefundItem,
                ) => {
                    setSelectedOption(nextModalOption);
                    setSelectedRefundOption(nextRefundOption);
                    setReturnPaymentSource(
                        nextRefundOption === RefundOption.RETURN_PAYMENT
                            ? ReturnPaymentSource.CHANGE_GUEST_QUANTITY
                            : null,
                    );
                    setRefundAmount(refundAmount);
                    setRefundItem(nextRefundItem ?? null);
                }}
            />
            {isCollectBalanceOptionSelected ? (
                <CollectPurchaseBalanceModal
                    purchase={modifiedPurchase ?? purchase}
                    event={event}
                    isOpen={isCollectBalanceOptionSelected}
                    onClose={() => {
                        if (shouldShowUnassignGuide) {
                            setPreviousEvent(event);
                            setSelectedOption(PurchaseActionOption.UNASSIGN_GUIDE);
                        } else {
                            handleFlushPurchaseCache();
                            resetSelectedOption();
                        }
                    }}
                />
            ) : null}
            <RefundOptionsModal
                purchase={purchase}
                isOpen={selectedOption === PurchaseActionOption.REFUND}
                onClose={() => {
                    handleFlushPurchaseCache();
                    resetSelectedOption();
                    if (!stayModalOpen) {
                        setStayModalOpen(true);
                    }
                }}
                onConfirm={(option) => {
                    setSelectedRefundOption(option);
                    resetSelectedOption();
                    setRefundAmount(null);
                }}
            />
            <SwitchExperienceModal
                purchase={purchase}
                item={item}
                isOpen={selectedOption === PurchaseActionOption.SWITCH_EXPERIENCE}
                onClose={(newPurchase, nextRefundItem) =>
                    handleCloseAfterPurchaseChange(newPurchase, ReturnPaymentSource.SWITCH_EXPERIENCE, nextRefundItem)
                }
            />
            <SplitPackageModal
                purchase={purchase}
                isOpen={selectedOption === PurchaseActionOption.SPLIT_PACKAGE}
                onClose={(newPurchase) =>
                    handleCloseAfterPurchaseChange(newPurchase, ReturnPaymentSource.SPLIT_PACKAGE)
                }
            />
            <CancelPurchaseItemModal
                purchase={purchase}
                item={selectedItem as ExperiencePurchaseItem | MerchandisePurchaseItem}
                isOpen={selectedOption === PurchaseActionOption.CANCEL_RESERVATION}
                onConfirm={() => setStayModalOpen(false)}
                onClose={(type, showRefundModal, newPurchase, events) => {
                    if (type === "confirm") {
                        const showRemoveMembershipCodeModal = selectedItem
                            ? !!getMembershipDiscountLineItem(selectedItem)
                            : getPurchaseItemsHavingMembershipCodeDiscount(purchase).length > 0;

                        if (showRemoveMembershipCodeModal) {
                            setCancelReservationModalData({ showRefundModal, newPurchase, events });
                            setSelectedOption(PurchaseActionOption.REMOVE_MEMBERSHIP_CODE);
                            return;
                        }
                    }

                    handleCancelReservationClose(showRefundModal, newPurchase, events);
                }}
            />
            <EditGratuityModal
                purchase={purchase}
                item={(selectedItem ?? firstGratuityEligibleItem) as ExperiencePurchaseItem}
                isOpen={selectedOption === PurchaseActionOption.MODIFY_GRATUITY}
                onClose={handleCloseAfterPurchaseChange}
            />
            <RemoveMembershipCodeModal
                isOpen={selectedOption === PurchaseActionOption.REMOVE_MEMBERSHIP_CODE}
                purchase={purchase}
                item={selectedItem}
                onClose={(updatedPurchase) => {
                    if (cancelReservationModalData) {
                        const { showRefundModal, newPurchase, events } = cancelReservationModalData;
                        handleCancelReservationClose(showRefundModal, updatedPurchase ?? newPurchase, events);
                    }
                }}
            />
            {purchase.splitPayment.enabled && !isOneBuyerWithSamePaymentMethod(purchase) ? (
                <SplitPayRefundModal
                    purchase={purchase}
                    item={selectedItem!}
                    shouldReduce={selectedRefundOption === RefundOption.REDUCE_AND_RETURN}
                    isOpen={
                        selectedRefundOption === RefundOption.RETURN_PAYMENT ||
                        selectedRefundOption === RefundOption.REDUCE_AND_RETURN
                    }
                    onClose={() => {
                        setSelectedRefundOption(null);
                        if (shouldShowUnassignGuide) {
                            setPreviousEvent(event);
                            setSelectedOption(PurchaseActionOption.UNASSIGN_GUIDE);
                        } else {
                            resetSelectedRefundOption();
                        }
                    }}
                />
            ) : (
                <PurchaseReturnPaymentModal
                    purchase={modifiedPurchase ?? purchase}
                    item={refundModalItem}
                    event={event}
                    refundAmount={refundModalAmount}
                    manualRefund={(amount: number) => setManualRefundAmount(amount)}
                    returnPaymentSource={returnPaymentSource}
                    shouldReduce={
                        selectedRefundOption === RefundOption.REDUCE_AND_RETURN ||
                        selectedRefundOption === RefundOption.REFUND_PAYMENT ||
                        selectedOption === PurchaseActionOption.REFUND_GIFT
                    }
                    isOpen={
                        selectedRefundOption === RefundOption.RETURN_PAYMENT ||
                        selectedRefundOption === RefundOption.REDUCE_AND_RETURN ||
                        selectedOption === PurchaseActionOption.REFUND_GIFT_OR_STORE_CREDIT ||
                        selectedOption === PurchaseActionOption.REFUND_GIFT ||
                        selectedRefundOption === RefundOption.REFUND_PAYMENT
                    }
                    returnAll={returnAll}
                    refundOption={selectedRefundOption}
                    isCancelAction={isCancelAction}
                    onClose={() => {
                        setSelectedRefundOption(null);
                        setRefundItem(null);
                        if (shouldShowUnassignGuide && !previousEvent) {
                            setPreviousEvent(event);
                            setSelectedOption(PurchaseActionOption.UNASSIGN_GUIDE);
                        } else if (previousEvent) {
                            if (isArray(previousEvent) && previousEvent.some((event) => event.guides.length > 0)) {
                                setSelectedOption(PurchaseActionOption.UNASSIGN_GUIDE);
                            }
                        } else {
                            resetSelectedRefundOption();
                            resetSelectedOption();
                        }
                        handleFlushPurchaseCache();
                        setReturnAll(false);
                    }}
                    onContinue={() => resetSelectedRefundOption}
                    onChangeRefundOption={(option) => setSelectedRefundOption(option)}
                    onError={(purchase) => {
                        handleCloseAfterPurchaseChange(purchase);
                    }}
                />
            )}
            <PurchaseManualRefundModal
                isOpen={manualRefundAmount > 0}
                purchase={purchase}
                amountToRefund={manualRefundAmount}
                onClose={() => setManualRefundAmount(0)}
            />
            <PurchaseReduceBookingValueModal
                purchase={purchase}
                item={item}
                isOpen={selectedRefundOption === RefundOption.REDUCE}
                returnAll={returnAll}
                isCancelAction={isCancelAction}
                onClose={resetSelectedRefundOption}
            />
            <EditPurchaseAddOnsModal
                purchase={purchase}
                item={item}
                isOpen={selectedOption === PurchaseActionOption.EDIT_ADD_ONS}
                onClose={(
                    nextModalOption: PurchaseActionOption | null,
                    nextRefundOption: RefundOption | null,
                    newPurchase?: Purchase,
                    nextRefundItem?: PurchaseItem,
                ) => {
                    if (newPurchase && (nextModalOption || nextRefundOption)) {
                        setModifiedPurchase(newPurchase);
                    }

                    setSelectedOption(nextModalOption);
                    setSelectedRefundOption(nextRefundOption);
                    setReturnPaymentSource(
                        nextRefundOption === RefundOption.RETURN_PAYMENT ? ReturnPaymentSource.CHANGE_ADD_ONS : null,
                    );
                    setRefundItem(nextRefundItem ?? null);
                    setRefundAmount(Math.abs(newPurchase?.balance ?? 0));
                    if (!nextModalOption && !nextRefundOption && newPurchase) {
                        updateCache("purchases", purchase);
                    }
                }}
            />
            <EditPurchasePrivacyModal
                purchase={purchase}
                item={item}
                isOpen={selectedOption === PurchaseActionOption.EDIT_PURCHASE_PRIVACY}
                onClose={(
                    nextModalOption: PurchaseActionOption | null,
                    nextRefundOption: RefundOption | null,
                    refundAmount,
                ) => {
                    setSelectedOption(nextModalOption);
                    setSelectedRefundOption(nextRefundOption);
                    setReturnPaymentSource(
                        nextRefundOption === RefundOption.RETURN_PAYMENT
                            ? ReturnPaymentSource.CHANGE_GUEST_QUANTITY
                            : null,
                    );
                    setRefundAmount(refundAmount);
                }}
            />
            <PastBookingWarningModal
                isOpen={warningModalOpen}
                onClose={() => setWarningModalOpen(false)}
                onContinue={() => {
                    setWarningModalOpen(false);
                    setSelectedOption(remainingAction as PurchaseActionOption);
                }}
            />
            <BookingEngineWarningModal
                bookingEngine={plugin?.name ?? purchase.source}
                isOpen={bookingEngineWarningModalOpen.isVisible}
                onClose={() => bookingEngineWarningModalOpen.hide()}
                onContinue={() => {
                    bookingEngineWarningModalOpen.hide();
                    setSelectedOption(remainingAction as PurchaseActionOption);
                }}
            />
            <CollectBalanceOptionsModal
                isOpen={selectedOption === PurchaseActionOption.COLLECT_BALANCE_OPTIONS}
                purchase={purchase}
                onClose={() => {
                    if (shouldShowUnassignGuide) {
                        setPreviousEvent(event);
                        setSelectedOption(PurchaseActionOption.UNASSIGN_GUIDE);
                    } else {
                        handleFlushPurchaseCache();
                        resetSelectedOption();
                    }
                }}
                onConfirm={(action: CollectBalanceOption) => {
                    if (action === CollectBalanceOption.COLLECT_NOW) {
                        setSelectedOption(PurchaseActionOption.COLLECT_BALANCE);
                    } else if (action === CollectBalanceOption.COLLECT_VIA_INVOICE) {
                        setSelectedOption(PurchaseActionOption.COLLECT_BALANCE_VIA_INVOICE);
                    } else if (shouldShowUnassignGuide) {
                        setPreviousEvent(event);
                        setSelectedOption(PurchaseActionOption.UNASSIGN_GUIDE);
                    } else {
                        handleFlushPurchaseCache();
                        resetSelectedOption();
                    }
                }}
            />
            <CollectPurchaseBalanceViaInvoiceModal
                purchase={purchase}
                event={event}
                item={item}
                isOpen={selectedOption === PurchaseActionOption.COLLECT_BALANCE_VIA_INVOICE}
                onClose={() => {
                    if (shouldShowUnassignGuide) {
                        setPreviousEvent(event);
                        setSelectedOption(PurchaseActionOption.UNASSIGN_GUIDE);
                    } else {
                        handleFlushPurchaseCache();
                        resetSelectedOption();
                    }
                }}
            />
            <AddFundsGiftModal
                purchase={purchase}
                item={item}
                isOpen={selectedOption === PurchaseActionOption.ADD_FUNDS}
                onClose={resetSelectedOption}
            />
            <AdjustBalanceModal
                purchase={purchase}
                item={item}
                isOpen={selectedOption === PurchaseActionOption.ADJUST_BALANCE}
                onClose={resetSelectedOption}
            />
            {/**
             *in print receipt action no need to eject the drawer. So setting default values
             */}
            <Printer
                enablePaperCut={false}
                enableDrawerEject={false}
                shouldStart={shouldPrintStart}
                onFinish={() => setShouldPrintStart(false)}
            >
                <PurchaseReceipt purchase={purchase} />
            </Printer>
            <GiftReceiptToPrint
                purchase={purchase}
                item={selectedItem as GiftPurchaseItem}
                action={selectedOption}
                onClosePrintDialog={resetSelectedOption}
            />
            <ReverseFundingModal
                isOpen={selectedRefundOption === RefundOption.REVERSE_FUNDING}
                storeCreditPurchase={purchase}
                onClose={() => {
                    resetSelectedRefundOption();
                    resetSelectedOption();
                }}
            />
            <ExportTicketsModal
                isOpen={selectedOption === PurchaseActionOption.EXPORT_TICKET}
                id={purchase.id}
                onClose={() => resetSelectedOption()}
            />
            <UpdateFulfilledQuantityModal
                purchase={modifiedPurchase?.id === purchase.id ? modifiedPurchase : purchase}
                isOpen={selectedOption === PurchaseActionOption.UPDATE_FULFILLED_QUANTITY}
                onClose={(newPurchase?: Purchase) => {
                    if (newPurchase) {
                        setModifiedPurchase(newPurchase);
                    }
                    resetSelectedOption();
                }}
            />
            {(isMerchandiseItem(item) || selectedRefundOption === RefundOption.CHANGE_MERCHANDISE_QUANTITY) &&
                merchandiseItem && (
                    <UpdateMerchandiseQuantityModal
                        purchase={purchase}
                        item={merchandiseItem as MerchandisePurchaseItem}
                        isOpen={
                            selectedOption === PurchaseActionOption.UPDATE_QUANTITY ||
                            selectedRefundOption === RefundOption.CHANGE_MERCHANDISE_QUANTITY
                        }
                        onClose={(
                            nextModalOption: PurchaseActionOption | null,
                            nextRefundOption: RefundOption | null,
                            refundAmount,
                            newPurchase?: Purchase,
                            nextRefundItem?: PurchaseItem,
                        ) => {
                            if (newPurchase) {
                                setModifiedPurchase(newPurchase);
                            }
                            setSelectedOption(nextModalOption);
                            setSelectedRefundOption(nextRefundOption);
                            setReturnPaymentSource(
                                nextRefundOption === RefundOption.RETURN_PAYMENT
                                    ? ReturnPaymentSource.CHANGE_MERCHANDISE_QUANTITY
                                    : null,
                            );
                            setRefundItem(nextRefundItem ?? null);
                            setRefundAmount(refundAmount);
                        }}
                    />
                )}
            <PostCheckInWarningModal />
        </div>
    );
};
